import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/lib/auth/AuthContext";
import { nextFromSearch } from "@/lib/auth/safeNext";

/**
 * Inverse of `PrivateRoute`. Wraps anonymous-only auth screens (login,
 * register, reset-password, etc.) and redirects already-authenticated
 * users away. Mid-flow MFA stays on this surface so the user can
 * complete /mfa-verify or /mfa-setup.
 *
 * Where to: the validated login deep link (`?next=`, billing-comms Teil 2a),
 * else "/". A signed-in visitor of `/auth/login?next=/event/…` goes straight
 * to the event. This is also the target of the login race: the form's own
 * `navigate` is a router transition, while `markAuthenticated` is an urgent
 * update, so this guard may render first and its `<Navigate>` would act last —
 * it must therefore pick the same place the form picks. (The login form now
 * flips the state and navigates in ONE transition, so the race does not open
 * on that lane; this keeps the two in agreement regardless.)
 */
export function PublicOnlyRoute({ children }: { children?: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state.kind === "authenticated") {
    return <Navigate to={nextFromSearch(location.search) ?? "/"} replace />;
  }
  return children ? <>{children}</> : <Outlet />;
}
